import * as React from 'react';

import { enumCurrencyCode, Unit } from '@rematter/frontend-lib-api-client';
import {
  humanReadableShortPricingUnit,
  TouchNumberField,
} from '@rematter/ui-web';

import { ItemFieldProps } from '../_types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { toMoney } from '~/modules/trade/utils/money';
import { useUIPreferences } from '~/shared/hooks';

//
// ⚛️ ⚛️ ⚛️ UNIT PRICE FIELD ⚛️ ⚛️ ⚛️
//
export const UnitPriceField: React.FC<ItemFieldProps> = ({
  lineItem,
  disabled = false,
  onUpdateItem,
  onKeyDown,
}) => {
  const t = useTranslations();
  const { touchModeEnabled } = useUIPreferences();

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [manualEntry, setManualEntry] = React.useState(false);

  React.useEffect(() => {
    if (lineItem.unitPrice != null) {
      setManualEntry(false);
    }
  }, [lineItem.unitPrice]);

  React.useEffect(() => {
    setManualEntry(false);
  }, [lineItem.itemId]);

  React.useEffect(() => {
    if (!manualEntry) return;
    const input = containerRef.current?.querySelector('input');
    if (input) {
      input.focus();
      input.select();
    }
  }, [manualEntry]);

  const handleChange = React.useCallback(
    (value: number) => {
      const currencyCode =
        lineItem.unitPrice?.currencyCode ?? enumCurrencyCode.USD;

      onUpdateItem(lineItem.id, {
        unitPrice: toMoney({
          amount: value,
          currencyCode,
        }),
      });
    },
    [lineItem.id, lineItem.unitPrice?.currencyCode, onUpdateItem],
  );

  const showTbd =
    lineItem.unitPrice == null && lineItem.itemId !== null && !manualEntry;

  if (showTbd) {
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setManualEntry(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (!disabled) setManualEntry(true);
          }
        }}
        className="@UnitPriceField flex h-full cursor-pointer items-center justify-end px-2 text-sm text-neutral/40 hover:text-neutral/60"
      >
        {t.scaleTicketPro.columns.priceTbd}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="@UnitPriceField h-full">
      <TouchNumberField
        aria-label={t.scaleTicketPro.columns.unitPrice}
        isDisabled={disabled}
        value={lineItem.unitPrice?.amount ?? 0}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        prefix="$"
        suffix={
          lineItem.pricingUnit
            ? `/${humanReadableShortPricingUnit(lineItem.pricingUnit as Unit)}`
            : undefined
        }
        formatOptions={{
          minimumFractionDigits: 2,
          maximumFractionDigits: 8,
        }}
        touchModeEnabled={touchModeEnabled}
      />
    </div>
  );
};
